import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CLASSES = ["Nursery","LKG","UKG","1","2","3","4","5","6","7","8","9","10","11","12"];

export default function Exams() {
  const [subjects, setSubjects] = useState([]);
  const [exams, setExams] = useState([]);
  const [students, setStudents] = useState([]);
  const [results, setResults] = useState({});
  const [subj, setSubj] = useState({ name: "", code: "", class_name: "1" });
  const [ex, setEx] = useState({ name: "", class_name: "1", exam_date: new Date().toISOString().slice(0,10), max_marks: 100, status: "scheduled" });
  const [selectedExam, setSelectedExam] = useState("");

  const load = async () => {
    const [su, ex, st] = await Promise.all([api.get("/subjects"), api.get("/exams"), api.get("/students")]);
    setSubjects(su.data); setExams(ex.data); setStudents(st.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selectedExam) return;
    api.get("/results", { params: { exam_id: selectedExam } }).then((r) => {
      const map = {};
      r.data.forEach((res) => { map[res.student_id] = res.subject_marks || {}; });
      setResults(map);
    });
  }, [selectedExam]);

  const addSubject = async () => {
    try { await api.post("/subjects", subj); toast.success("Subject added"); setSubj({ name: "", code: "", class_name: "1" }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delSubject = async (id) => { await api.delete(`/subjects/${id}`); load(); };

  const addExam = async () => {
    try { await api.post("/exams", { ...ex, max_marks: Number(ex.max_marks) }); toast.success("Exam created"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const currentExam = exams.find((e) => e.id === selectedExam);
  const examSubjects = currentExam ? subjects.filter((s) => s.class_name === currentExam.class_name) : [];
  const examStudents = currentExam ? students.filter((s) => s.class_name === currentExam.class_name) : [];

  const setMark = (studentId, subjectName, v) => {
    setResults({ ...results, [studentId]: { ...(results[studentId] || {}), [subjectName]: v } });
  };

  const saveResult = async (studentId) => {
    try {
      const marks = {};
      Object.entries(results[studentId] || {}).forEach(([k, v]) => { marks[k] = Number(v) || 0; });
      await api.post("/results", { exam_id: selectedExam, student_id: studentId, subject_marks: marks });
      toast.success("Saved");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const printResult = async (st) => {
    const marks = results[st.id] || {};
    // Fetch school for signatures
    let school = {};
    try { const uRes = await api.get("/auth/me"); if (uRes.data.school_id) { const sRes = await api.get(`/schools/${uRes.data.school_id}`); school = sRes.data; } } catch {}
    const total = Object.values(marks).reduce((a, b) => a + (Number(b) || 0), 0);
    const max = examSubjects.length * (currentExam?.max_marks || 100);
    const pct = max ? ((total / max) * 100).toFixed(1) : 0;
    const grade = pct >= 90 ? "A+" : pct >= 75 ? "A" : pct >= 60 ? "B" : pct >= 40 ? "C" : "F";
    const rows = examSubjects.map(sb => `<tr><td>${sb.name}</td><td style="text-align:right">${currentExam?.max_marks || 100}</td><td style="text-align:right"><b>${marks[sb.name] ?? "—"}</b></td></tr>`).join("");
    const w = window.open("", "_blank");
    w.document.write(`
      <html><head><title>Result - ${st.first_name}</title>
      <style>body{font-family:'Segoe UI',sans-serif;padding:40px;background:white}
      .doc{max-width:720px;margin:0 auto;border:3px double #0f172a;padding:32px}
      .head{text-align:center;border-bottom:3px solid #f59e0b;padding-bottom:14px;margin-bottom:20px}
      .head h1{margin:0;font-size:24px;color:#0f172a}
      .head p{margin:4px 0 0;color:#64748b;font-size:12px;letter-spacing:2px;text-transform:uppercase}
      .info{display:grid;grid-template-columns:1fr 1fr;gap:8px;margin-bottom:20px;font-size:14px}
      .info b{color:#64748b;font-weight:500}
      table{width:100%;border-collapse:collapse;margin-top:10px}
      th,td{border:1px solid #cbd5e1;padding:8px 12px;font-size:14px}
      th{background:#f1f5f9;text-align:left}
      .total{background:#fef3c7;font-weight:700}
      .sig{display:flex;justify-content:space-between;margin-top:60px;font-size:12px;text-align:center;color:#64748b}
      .sig img{height:40px;margin-bottom:4px}
      </style></head><body><div class="doc">
      <div class="head"><h1>${school.name || "School Name"}</h1><p>Academic Result — ${currentExam?.name || ""}</p></div>
      <div class="info">
        <div><b>Student Name:</b> <b style="color:#0f172a">${st.first_name} ${st.last_name || ""}</b></div>
        <div><b>Class:</b> <b style="color:#0f172a">${st.class_name} ${st.section || ""}</b></div>
        <div><b>Reg No:</b> <b style="color:#0f172a">${st.registration_no || "—"}</b></div>
        <div><b>Exam Date:</b> <b style="color:#0f172a">${currentExam?.exam_date || ""}</b></div>
      </div>
      <table><thead><tr><th>Subject</th><th style="text-align:right">Max</th><th style="text-align:right">Obtained</th></tr></thead>
      <tbody>${rows}<tr class="total"><td>TOTAL</td><td style="text-align:right">${max}</td><td style="text-align:right">${total}</td></tr>
      <tr class="total"><td colspan="2">PERCENTAGE / GRADE</td><td style="text-align:right">${pct}% · ${grade}</td></tr></tbody></table>
      <div class="sig">
        <div>${school.principal_signature_url ? `<img src="${school.principal_signature_url}" />` : ""}<br/>______________________<br/>${school.principal_name || "Principal"}</div>
        <div>${school.chairman_signature_url ? `<img src="${school.chairman_signature_url}" />` : ""}<br/>______________________<br/>${school.chairman_name || "Chairman"}</div>
      </div></div><script>setTimeout(()=>window.print(),500)</script></body></html>`);
  };

  return (
    <DashLayout variant="school" title="Exams & Results">
      <Tabs defaultValue="subjects">
        <TabsList>
          <TabsTrigger value="subjects" data-testid="tab-subjects">Subjects</TabsTrigger>
          <TabsTrigger value="exams" data-testid="tab-exams">Exams</TabsTrigger>
          <TabsTrigger value="results" data-testid="tab-results">Result Feeding</TabsTrigger>
        </TabsList>

        <TabsContent value="subjects">
          <Card className="p-4 flex gap-3 flex-wrap items-end mb-4">
            <Input placeholder="Subject name" value={subj.name} onChange={(e) => setSubj({ ...subj, name: e.target.value })} data-testid="sub-name" />
            <Input placeholder="Code" value={subj.code} onChange={(e) => setSubj({ ...subj, code: e.target.value })} data-testid="sub-code" />
            <Select value={subj.class_name} onValueChange={(v) => setSubj({ ...subj, class_name: v })}>
              <SelectTrigger className="w-32" data-testid="sub-class"><SelectValue /></SelectTrigger>
              <SelectContent>{CLASSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={addSubject} className="bg-blue-700 hover:bg-blue-800" data-testid="sub-add"><Plus className="w-4 h-4 mr-1" />Add</Button>
          </Card>
          <div className="bg-white border rounded-lg">
            <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Code</TableHead><TableHead>Class</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>{subjects.map((s) => <TableRow key={s.id}><TableCell>{s.name}</TableCell><TableCell>{s.code}</TableCell><TableCell>{s.class_name}</TableCell><TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => delSubject(s.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button></TableCell></TableRow>)}</TableBody></Table>
          </div>
        </TabsContent>

        <TabsContent value="exams">
          <Card className="p-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end mb-4">
            <Input placeholder="Exam Name" value={ex.name} onChange={(e) => setEx({ ...ex, name: e.target.value })} data-testid="ex-name" />
            <Select value={ex.class_name} onValueChange={(v) => setEx({ ...ex, class_name: v })}>
              <SelectTrigger data-testid="ex-class"><SelectValue /></SelectTrigger>
              <SelectContent>{CLASSES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="date" value={ex.exam_date} onChange={(e) => setEx({ ...ex, exam_date: e.target.value })} data-testid="ex-date" />
            <Input type="number" placeholder="Max Marks" value={ex.max_marks} onChange={(e) => setEx({ ...ex, max_marks: e.target.value })} data-testid="ex-max" />
            <Button onClick={addExam} className="bg-blue-700 hover:bg-blue-800" data-testid="ex-add"><Plus className="w-4 h-4 mr-1" />Create Exam</Button>
          </Card>
          <div className="bg-white border rounded-lg">
            <Table><TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Class</TableHead><TableHead>Date</TableHead><TableHead>Max</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>{exams.map(e => <TableRow key={e.id}><TableCell>{e.name}</TableCell><TableCell>{e.class_name}</TableCell><TableCell>{e.exam_date}</TableCell><TableCell>{e.max_marks}</TableCell><TableCell>{e.status}</TableCell></TableRow>)}</TableBody></Table>
          </div>
        </TabsContent>

        <TabsContent value="results">
          <Card className="p-4 mb-4">
            <Label className="mb-1 block text-xs">Select Exam</Label>
            <Select value={selectedExam} onValueChange={setSelectedExam}>
              <SelectTrigger className="max-w-md" data-testid="res-exam-select"><SelectValue placeholder="Choose exam" /></SelectTrigger>
              <SelectContent>{exams.map(e => <SelectItem key={e.id} value={e.id}>{e.name} · Class {e.class_name} · {e.exam_date}</SelectItem>)}</SelectContent>
            </Select>
          </Card>
          {selectedExam && (
            <div className="bg-white border rounded-lg overflow-x-auto">
              <Table>
                <TableHeader><TableRow><TableHead>Student</TableHead>{examSubjects.map(sb => <TableHead key={sb.id} className="text-right">{sb.name}</TableHead>)}<TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
                <TableBody>{examStudents.map((st) => (
                  <TableRow key={st.id}>
                    <TableCell className="font-semibold">{st.first_name} {st.last_name}</TableCell>
                    {examSubjects.map((sb) => <TableCell key={sb.id} className="text-right"><Input type="number" className="w-20 ml-auto tabular-nums" value={(results[st.id] || {})[sb.name] || ""} onChange={(e) => setMark(st.id, sb.name, e.target.value)} data-testid={`mark-${st.id}-${sb.id}`} /></TableCell>)}
                    <TableCell className="text-right space-x-1"><Button size="sm" onClick={() => saveResult(st.id)} data-testid={`save-result-${st.id}`}>Save</Button><Button size="sm" variant="outline" onClick={() => printResult(st)} data-testid={`print-result-${st.id}`}>Print</Button></TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
              {examSubjects.length === 0 && <div className="p-6 text-slate-500 text-sm">Add subjects for this class first.</div>}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </DashLayout>
  );
}
